'use client'

import { forwardRef, useCallback, useState } from 'react'
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { labelStyles, textStyles } from './styles'
import { mtc, TypographyStyles } from '@/styles'
import { Button } from '../button'
import { Label } from '../label'
import { Icon } from '../icon'
import { List } from '../list'
import { Text } from '../text'

type FileInputRenderFunction = (props: {
  files: File[]
  onRemove: (index: number) => void
}) => React.ReactNode

export interface IFileInput extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'children'
> {
  className?: string
  disabled?: boolean
  error?: boolean
  errorTestId?: string
  label?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  children?: React.ReactNode | FileInputRenderFunction
}

/** An input for uploading files */
export const FileInput = forwardRef<HTMLInputElement, IFileInput>(
  (
    {
      className,
      disabled = false,
      error,
      errorTestId,
      label = 'Choose File',
      onChange,
      placeholder,
      children,
      ...props
    },
    ref
  ) => {
    const [documentsToUpload, setDocumentsToUpload] = useState<File[]>([])

    const handleDocumentsToUpload = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const docs = event.target.files

      if (docs) {
        setDocumentsToUpload((prev) => [...prev, ...Array.from(docs)])
      }

      onChange(event)
    }

    const handleDelete = useCallback(
      (fileIndex: number) => {
        const updatedFiles = documentsToUpload.filter(
          (_, index) => index !== fileIndex
        )
        setDocumentsToUpload(updatedFiles)

        if (
          updatedFiles.length === 0 &&
          ref &&
          'current' in ref &&
          ref.current
        ) {
          ref.current.value = ''
        }
      },
      [documentsToUpload, ref]
    )

    return (
      <div className={`relative ${className}`}>
        <input
          id="doc-upload"
          ref={ref}
          type="file"
          className="opacity-0 w-0 h-0"
          placeholder={placeholder}
          disabled={disabled}
          data-testid={errorTestId}
          onChange={handleDocumentsToUpload}
          multiple
          {...props}
        />

        <div className="flex flex-col items-start gap-2">
          <Label
            htmlFor="doc-upload"
            disabled={disabled}
            className={mtc(labelStyles({ disabled, error }))}
          >
            <Button disabled={disabled} variant="secondary" asChild>
              <span className="w-fit">{label}</span>
            </Button>
          </Label>

          {documentsToUpload.length === 0 ? (
            <Text>No files chosen</Text>
          ) : children ? (
            typeof children === 'function' ? (
              children({ files: documentsToUpload, onRemove: handleDelete })
            ) : (
              children
            )
          ) : (
            <>
              <Text variant={TypographyStyles.EYEBROW}>Files to upload</Text>

              <List className="pl-0 w-full">
                {documentsToUpload.map((doc: File, index: number) => (
                  <List.Item
                    key={`${doc.name}-${doc.lastModified}`}
                    className="flex flex-row items-center gap-2 w-full"
                  >
                    <Text
                      className={mtc(
                        textStyles({
                          error,
                          isFileUploaded: documentsToUpload.length > 0,
                        })
                      )}
                    >
                      {doc.name}
                    </Text>

                    <Icon
                      icon={faTrashCan}
                      onClick={() => handleDelete(index)}
                      className="shrink-0 cursor-pointer"
                    />
                  </List.Item>
                ))}
              </List>
            </>
          )}
        </div>
      </div>
    )
  }
)

FileInput.displayName = 'FileInput'
